/**
 * The people who signed up through this tenant.
 *
 * Every other screen in this console is about AGENTS — the fleet, its mandates,
 * what it spent. This one is about humans, and it is the screen an operator
 * reaches for when a real person emails support saying "I can't get in".
 *
 * ── The one distinction this screen exists to make ───────────────────────────
 * "Signed up" and "confirmed" are different facts, and conflating them is how a
 * product convinces itself it can reach people it cannot. An account can hold an
 * email nobody ever proved — under identify-only sign-in that is the NORMAL
 * case, because typing an address IS the sign-in. So the counts separate
 * `with_email` from `verified_email`, and every row says which of its identifiers
 * were actually demonstrated.
 *
 * ── Test accounts ────────────────────────────────────────────────────────────
 * Any real product's user table fills up with its own seeds. Marking one is a
 * click and it is reversible; ERASING one is neither, so the two are separate
 * steps and the second is gated on the first — by the SERVER, not by this bundle.
 * The console cannot send the flag that would let it erase a real person's
 * account, which is what makes the typed confirmation below more than theatre.
 *
 * ── Metadata ─────────────────────────────────────────────────────────────────
 * A scratchpad hung off the identity record: any valid JSON object, nothing
 * enforced beyond that and a 16KB ceiling. It SAVES WHOLE rather than merging,
 * because an operator editing raw JSON is looking at the entire document and
 * expects what they see to be what lands — a merge would make deleting a key
 * impossible from the only interface that edits it.
 */

import { useEffect, useRef, useState } from "react";
import { State, useAsync } from "@authoxi/ui-shared";
import type { Api, EndUser, Env, TenantApp } from "../api";

const PAGE = 25;

type Filter = "all" | "verified" | "unverified";
type EnvFilter = Env | "all";

export function Users({ api }: { api: Api }) {
  const [query, setQuery] = useState("");
  // What is typed vs what is being searched. Firing a request per keystroke would
  // put the operator's half-typed address in the server log and make the list
  // flicker through three wrong answers on the way to the right one.
  const [applied, setApplied] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [hideTest, setHideTest] = useState(false);
  const [appId, setAppId] = useState("");
  // Defaults to `all` rather than to either environment. An operator opening this
  // screen is asking "who signed up", and silently answering for one half — with a
  // control they had not noticed yet — is how somebody concludes their users are gone.
  const [env, setEnv] = useState<EnvFilter>("all");
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<EndUser | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = () => setNonce((n) => n + 1);

  const apps = useAsync(() => api.apps(), []);
  const stats = useAsync(() => api.userStats(), [nonce]);
  const page = useAsync(
    () =>
      api.users({
        limit: PAGE,
        offset,
        q: applied || undefined,
        verified: filter === "all" ? undefined : filter === "verified",
        is_test: hideTest ? false : undefined,
        app_id: appId || undefined,
        env,
      }),
    [applied, filter, hideTest, appId, env, offset, nonce],
  );

  // A new predicate means page 1. Staying on page 3 of a filter that now matches
  // two people shows an empty list and looks like a broken screen.
  const repredicate = (fn: () => void) => {
    fn();
    setOffset(0);
    setOpenId(null);
  };

  const total = page.data?.total ?? 0;
  const shown = page.data?.users ?? [];
  const filtered =
    applied !== "" || filter !== "all" || hideTest || appId !== "" || env !== "all";
  const appList = apps.data?.apps ?? [];
  // The filter only earns its place once there is more than one door to choose
  // between. A single-app tenant should not be asked which app it means.
  const manyApps = appList.length > 1;
  const appName = (id: string) => appList.find((a) => a.app_id === id)?.name ?? id;

  return (
    <>
      <div className="view-head">
        <span className="label">Users</span>
        <h1>Everyone signed up with you.</h1>
        <p>
          People who authenticated through your tenant. <b>Signed up is not the
          same as confirmed</b> — an address is only confirmed once somebody
          answered a code sent to it, and until then it is an address they typed.
        </p>
      </div>

      {stats.data && (
        <div className="userstats" data-testid="user-stats">
          <Stat
            n={stats.data.total_users}
            label="accounts"
            note={
              stats.data.test_users > 0
                ? `${stats.data.test_users.toLocaleString()} marked as test`
                : undefined
            }
          />
          <Stat n={stats.data.verified_email} of={stats.data.with_email} label="confirmed email" />
          <Stat n={stats.data.verified_phone} of={stats.data.with_phone} label="confirmed phone" />
          <Stat n={stats.data.active_last_30d} label="signed in, 30d" />
        </div>
      )}

      <AppStrip
        api={api}
        apps={appList}
        counts={stats.data?.by_app ?? {}}
        total={stats.data?.total_users ?? 0}
        selected={appId}
        env={env}
        showTiles={manyApps}
        onSelect={(id) => repredicate(() => setAppId(id))}
        onChanged={() => {
          apps.reload();
          reload();
        }}
      />

      <EnvBar
        value={env}
        live={stats.data?.live_users ?? 0}
        dev={stats.data?.dev_users ?? 0}
        onChange={(next) => repredicate(() => setEnv(next))}
      />

      <div className="ufilters">
        <form
          className="ufilters__search"
          onSubmit={(e) => {
            e.preventDefault();
            repredicate(() => setApplied(query.trim()));
          }}
        >
          <input
            className="input"
            type="search"
            placeholder="Search email, phone or name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search users"
            data-testid="user-search"
          />
          <button className="btn" type="submit">
            Search
          </button>
        </form>

        <div className="ufilters__right">
          <div className="segbar" role="group" aria-label="Filter by confirmation">
            {(
              [
                ["all", "All"],
                ["verified", "Confirmed"],
                ["unverified", "Not confirmed"],
              ] as [Filter, string][]
            ).map(([v, label]) => (
              <button
                key={v}
                type="button"
                className={filter === v ? "on" : ""}
                aria-pressed={filter === v}
                onClick={() => repredicate(() => setFilter(v))}
                data-testid={`filter-${v}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* A checkbox and not a fourth segment: it is an independent dimension.
              "Confirmed" and "Hide test" answer different questions and an operator
              routinely wants both at once. */}
          <label className="checkchip" data-testid="hide-test">
            <input
              type="checkbox"
              checked={hideTest}
              onChange={(e) => repredicate(() => setHideTest(e.target.checked))}
            />
            <span>Hide test</span>
          </label>

          {filtered && (
            <button
              className="btn btn-quiet"
              type="button"
              onClick={() =>
                repredicate(() => {
                  setQuery("");
                  setApplied("");
                  setFilter("all");
                  setHideTest(false);
                  setAppId("");
                })
              }
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {page.loading && !page.data && <State kind="loading" title="Loading users…" />}
      {page.error && <State kind="error" title="Could not load users" detail={page.error} />}
      {page.data && shown.length === 0 && (
        <State
          kind="empty"
          title={filtered ? "Nobody matches that." : "No users yet."}
          detail={
            filtered
              ? "Try a different search, or clear the filter."
              : "The first person to sign in through your app appears here."
          }
        />
      )}

      {shown.length > 0 && (
        <div className="card" data-testid="user-table">
          <table className="utable">
            <thead>
              <tr>
                <th>Person</th>
                {manyApps && <th className="u-col-app">App</th>}
                <th>How they sign in</th>
                <th className="u-col-joined">Joined</th>
                <th className="u-col-act">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((u) => (
                <UserRow
                  key={u.user_id}
                  api={api}
                  user={u}
                  apps={appList}
                  open={openId === u.user_id}
                  onToggle={() => setOpenId(openId === u.user_id ? null : u.user_id)}
                  onChanged={reload}
                  onDelete={() => setToDelete(u)}
                  appLabel={manyApps ? appName(u.app_id) : null}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE && (
        <div className="upager">
          <span className="sm muted" data-testid="user-range">
            {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
          </span>
          <span className="row">
            <button
              className="btn btn-quiet"
              disabled={offset === 0}
              onClick={() => {
                setOffset(Math.max(0, offset - PAGE));
                setOpenId(null);
              }}
            >
              Previous
            </button>
            <button
              className="btn btn-quiet"
              disabled={offset + PAGE >= total}
              onClick={() => {
                setOffset(offset + PAGE);
                setOpenId(null);
              }}
            >
              Next
            </button>
          </span>
        </div>
      )}

      {toDelete && (
        <DeleteDialog
          api={api}
          user={toDelete}
          onClose={() => setToDelete(null)}
          onDone={() => {
            setToDelete(null);
            setOpenId(null);
            reload();
          }}
        />
      )}
    </>
  );
}

/**
 * The tenant's front doors, and the filter, as one control.
 *
 * There was a separate Apps screen. Two screens for one question — "who signed up
 * with me, and where" — meant checking a publishable key and checking who was
 * behind it were different journeys, and neither page could answer the other's
 * half. The apps ARE the filter, so they are drawn as one.
 *
 * The selected app opens a line carrying its publishable key and whether its
 * accounts are its own: the two facts the old screen existed to show, in the place
 * you are already looking.
 */
function AppStrip({
  api,
  apps,
  counts,
  total,
  selected,
  env,
  showTiles,
  onSelect,
  onChanged,
}: {
  api: Api;
  apps: TenantApp[];
  counts: Record<string, { dev: number; live: number }>;
  total: number;
  selected: string;
  env: EnvFilter;
  showTiles: boolean;
  onSelect: (id: string) => void;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const chosen = apps.find((a) => a.app_id === selected);
  // A tile shows the count for whatever environment is being viewed, so the number
  // under an app always describes the list underneath it. Showing a lifetime total
  // above a filtered list is how two numbers on one screen disagree.
  const tileCount = (id: string) => {
    const c = counts[id] ?? { dev: 0, live: 0 };
    return env === "all" ? c.dev + c.live : c[env];
  };
  const allCount =
    env === "all" ? total : apps.reduce((n, a) => n + tileCount(a.app_id), 0);

  // The tiles are also the app manager: a tenant with one app still needs somewhere
  // to add a second, so the strip always renders. `showTiles` only decides whether
  // the "All apps" tile is worth offering — with one app it filters nothing.
  return (
    <section className="appstrip" data-testid="app-strip">
      <span className="label appstrip__label">Apps</span>
      <div className="appstrip__tiles">
        {showTiles && (
          <button
            type="button"
            className={`apptile${selected === "" ? " on" : ""}`}
            aria-pressed={selected === ""}
            onClick={() => onSelect("")}
            data-testid="app-tile-all"
          >
            <span className="apptile__n">{allCount.toLocaleString()}</span>
            <span className="apptile__name">All apps</span>
          </button>
        )}
        {apps.map((a) => (
          <button
            key={a.app_id}
            type="button"
            className={`apptile${selected === a.app_id ? " on" : ""}${
              a.status === "suspended" ? " off" : ""
            }`}
            aria-pressed={selected === a.app_id}
            onClick={() => onSelect(a.app_id)}
            data-testid={`app-tile-${a.slug}`}
          >
            <span className="apptile__n">{tileCount(a.app_id).toLocaleString()}</span>
            <span className="apptile__name">{a.name}</span>
            {a.status === "suspended" && <span className="apptile__env">suspended</span>}
          </button>
        ))}
        <button
          type="button"
          className="apptile apptile--new"
          onClick={() => setAdding(true)}
          data-testid="app-new"
        >
          <span className="apptile__plus" aria-hidden="true">
            +
          </span>
          <span className="apptile__name">New app</span>
        </button>
      </div>
      {adding && (
        <NewApp
          api={api}
          onCancel={() => setAdding(false)}
          onCreated={(id) => {
            setAdding(false);
            onChanged();
            onSelect(id);
          }}
        />
      )}

      {chosen && (
        <div className="appstrip__detail" data-testid="app-detail">
          <span className="label">Publishable key</span>
          {/* Publishable BY DESIGN — it identifies the app to a browser and grants
              nothing on its own, so it is shown in full. Masking a public value
              teaches people to treat it like a secret. */}
          <code className="mono appstrip__key">{chosen.publishable_key}</code>
          <span className="sm muted appstrip__note">
            {chosen.shares_accounts_with.length === 0 ? (
              <>Accounts here are its own — nothing signed in reaches another app.</>
            ) : (
              <>
                Shares accounts with {chosen.shares_accounts_with.length} other app
                {chosen.shares_accounts_with.length === 1 ? "" : "s"} — one sign-in
                across them.
              </>
            )}
          </span>
          <AppSettings
            api={api}
            app={chosen}
            counts={counts[chosen.app_id] ?? { dev: 0, live: 0 }}
            onChanged={onChanged}
            onDeleted={() => {
              onSelect("");
              onChanged();
            }}
          />
        </div>
      )}
    </section>
  );
}

/**
 * Adding a front door.
 *
 * One question: where is it served from. Everything else derives — the label from
 * the host, the internal handle from the label, and the app's origin allow-list
 * IS this list, so an app is correctly configured the moment it exists.
 *
 * This used to ask for a name, a slug and an account-sharing choice, then ask for
 * the URL later, separately. Three answers an operator had to invent, before the
 * one they already had; and because the real one came last it was the one that
 * got skipped.
 */
function NewApp({
  api,
  onCancel,
  onCreated,
}: {
  api: Api;
  onCancel: () => void;
  onCreated: (appId: string) => void;
}) {
  const [urls, setUrls] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const lines = urls
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  // Shown as the label the app will get, so nobody has to guess what we derive.
  const derived = (() => {
    try {
      return lines[0] ? new URL(lines[0]).hostname : "";
    } catch {
      return "";
    }
  })();

  const create = async () => {
    setBusy(true);
    setErr(null);
    try {
      const made = await api.createApp({
        urls: lines,
        ...(name.trim() ? { name: name.trim() } : {}),
      });
      onCreated(made.app_id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not create the app.");
      setBusy(false);
    }
  };

  return (
    <div className="appstrip__detail newapp" data-testid="app-new-form">
      <label className="newapp__field">
        <span className="label">Where is it served from?</span>
        <textarea
          className="panel__textarea mono"
          rows={2}
          autoFocus
          value={urls}
          spellCheck={false}
          onChange={(e) => setUrls(e.target.value)}
          placeholder={"https://app.example.com\nhttps://m.example.com"}
          data-testid="app-new-urls"
        />
      </label>
      <p className="sm muted newapp__note">
        One per line, exact — list each subdomain rather than using a wildcard, because
        this is also where sign-ins and password-reset links are allowed to return.
        {derived && (
          <>
            {" "}
            It will be called <b data-testid="app-new-derived">{name.trim() || derived}</b>.
          </>
        )}{" "}
        A mobile or server-side client has no URL — give it a name instead.
      </p>
      <label className="newapp__field newapp__share">
        <span className="label">Label (optional)</span>
        <input
          className="input"
          value={name}
          placeholder={derived || "Mobile app"}
          onChange={(e) => setName(e.target.value)}
          data-testid="app-new-name"
        />
      </label>
      {err && (
        <p className="sm err" data-testid="app-new-error">
          {err}
        </p>
      )}
      <div className="panel__actions">
        <button
          type="button"
          className="btn btn-solid panel__btn"
          disabled={busy || (lines.length === 0 && !name.trim())}
          onClick={() => void create()}
          data-testid="app-new-save"
        >
          {busy ? "Creating…" : "Create app"}
        </button>
        <button type="button" className="btn btn-quiet panel__btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Renaming, suspending and retiring an app.
 *
 * ── Why delete is not simply a button ────────────────────────────────────────
 * Deleting an app does not delete its accounts, and it must not: they are people.
 * But leaving them behind a door that no longer opens is worse than either — they
 * become unreachable, invisible, and still holding their addresses in a space
 * nothing can sign in to. So the server refuses while any account remains, and this
 * panel says so BEFORE the click rather than after, with the counts and the way out.
 *
 * Suspend is offered first for the same reason. "I want this app closed" is almost
 * always what somebody means, and suspending does that reversibly and immediately;
 * deletion is for a door that is genuinely finished with.
 */
function AppSettings({
  api,
  app,
  counts,
  onChanged,
  onDeleted,
}: {
  api: Api;
  app: TenantApp;
  counts: { dev: number; live: number };
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [name, setName] = useState(app.name);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [wiping, setWiping] = useState(false);

  useEffect(() => {
    setName(app.name);
    setErr(null);
  }, [app.app_id, app.name]);

  const total = counts.dev + counts.live;
  const isDefault = app.slug === "default";
  const suspended = app.status === "suspended";

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="appsettings" data-testid="app-settings">
      <label className="appsettings__rename">
        <span className="label">Name</span>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="app-name-input"
        />
      </label>
      <div className="appsettings__actions">
        <button
          type="button"
          className="btn btn-solid panel__btn"
          disabled={busy || !name.trim() || name === app.name}
          onClick={() => void run(() => api.updateApp(app.app_id, { name: name.trim() }))}
          data-testid="app-rename-save"
        >
          Save name
        </button>
        <button
          type="button"
          className="btn btn-quiet panel__btn"
          disabled={busy || isDefault}
          onClick={() =>
            void run(() =>
              api.updateApp(app.app_id, { status: suspended ? "active" : "suspended" }),
            )
          }
          data-testid="app-suspend"
        >
          {suspended ? "Reactivate" : "Suspend"}
        </button>
        {counts.dev > 0 && (
          <button
            type="button"
            className="btn btn-danger panel__btn"
            disabled={busy}
            onClick={() => setWiping(true)}
            data-testid="dev-reset-open"
          >
            Wipe {counts.dev.toLocaleString()} dev account{counts.dev === 1 ? "" : "s"}
          </button>
        )}
        {!isDefault && (
          <button
            type="button"
            className="btn btn-danger panel__btn"
            disabled={busy || total > 0}
            onClick={() => setConfirming(true)}
            title={total > 0 ? "This app still has accounts behind it" : undefined}
            data-testid="app-delete"
          >
            Delete app
          </button>
        )}
      </div>
      <p className="sm muted appsettings__note">
        {isDefault ? (
          <>
            This is the default app — the tenant&rsquo;s original publishable key resolves
            through it, so it cannot be suspended or deleted.
          </>
        ) : suspended ? (
          <>
            <b>Suspended.</b> Its key does not resolve, so nobody can sign in here. Its
            accounts are untouched and come back with it.
          </>
        ) : total > 0 ? (
          <>
            <b>
              {total.toLocaleString()} account{total === 1 ? "" : "s"} behind this app
            </b>
            {counts.live > 0 && counts.dev > 0
              ? ` (${counts.live} live, ${counts.dev} dev)`
              : ""}
            , so it cannot be deleted — they would be left holding their addresses
            behind a key that no longer works.
            {counts.dev > 0 && " Wipe the dev accounts"}
            {counts.live > 0 &&
              `${counts.dev > 0 ? ", and ask us to move" : " Ask us to move"} the live ones to another app or erase them`}
            . To just close it, suspend it instead.
          </>
        ) : (
          <>Nothing is behind this app, so it can be retired. Its URLs are freed.</>
        )}
      </p>
      <p className="sm muted appsettings__note">
        Sign-ins from <span className="mono">localhost</span> land in this app&rsquo;s
        <b> dev</b> accounts; everything else is <b>live</b>. There is nothing to set
        up — every app has both from the moment it exists.
      </p>
      <AppUrls api={api} app={app} onChanged={onChanged} />
      {err && (
        <p className="sm err" data-testid="app-settings-error">
          {err}
        </p>
      )}
      {wiping && (
        <ConfirmDialog
          title="Wipe all dev data?"
          phrase={app.slug}
          confirmLabel="Wipe dev data"
          onCancel={() => setWiping(false)}
          onConfirm={async () => {
            await api.resetDev(app.app_id);
            setWiping(false);
            onChanged();
          }}
          body={
            <>
              This erases{" "}
              <b>
                {counts.dev.toLocaleString()} dev account
                {counts.dev === 1 ? "" : "s"}
              </b>{" "}
              in {app.name}, with every identifier and session. It cannot be undone.
              Live accounts are not touched.
            </>
          }
        />
      )}
      {confirming && (
        <ConfirmDialog
          title="Delete this app?"
          // The app's own name, because that is what is on the screen in front of
          // them. Asking for a derived internal handle would make somebody hunt for
          // a value they never chose, which is friction without safety.
          phrase={app.name}
          confirmLabel="Delete app"
          onCancel={() => setConfirming(false)}
          onConfirm={async () => {
            await api.deleteApp(app.app_id);
            setConfirming(false);
            onDeleted();
          }}
          body={
            <>
              <b>{app.name}</b> stops accepting sign-ins immediately and its publishable
              key stops resolving, and its URLs are freed for another app to claim.
            </>
          }
        />
      )}
    </div>
  );
}

/**
 * The app's URLs.
 *
 * ── One field, two jobs, deliberately ────────────────────────────────────────
 * This is both where the app is served from and the list of places its sessions
 * and password-reset links may be delivered. They were separate — an app had a
 * name and a slug, and an origin allow-list configured somewhere else, later — and
 * separating them meant the security-relevant half was optional in practice and
 * usually empty.
 *
 * ── Why this one IS configured, when the environment rule is not ─────────────
 * They look similar and are not. The environment rule decides which drawer an
 * account is filed in, and getting it wrong misfiles a row — which is why it was
 * simplified to have no configuration at all. This decides where a live session
 * and a raw reset token may be DELIVERED, and getting it wrong hands somebody a
 * credential. No rule we could infer is safe enough to replace being told.
 *
 * An app with no URLs inherits its tenant's list, which is what keeps every
 * integration predating this working. The panel says which of the two is in force,
 * because "it works and I don't know why" is the state that gets edited into "it
 * doesn't work and I don't know why".
 */
function AppUrls({
  api,
  app,
  onChanged,
}: {
  api: Api;
  app: TenantApp;
  onChanged: () => void;
}) {
  const own = app.urls ?? [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(own.join("\n"));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setDraft(own.join("\n"));
    setEditing(false);
    setErr(null);
    // Keyed on the content rather than the array, which is rebuilt every render.
  }, [app.app_id, own.join("\n")]);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.updateApp(app.app_id, {
        urls: draft
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean),
      });
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="appsettings__origins" data-testid="app-urls">
      <span className="label">URLs</span>
      {!editing ? (
        <>
          {own.length === 0 ? (
            <span className="sm muted" data-testid="urls-none">
              {app.inherits_urls ? (
                <>
                  None of its own — using your tenant-wide list. Sign-ins and
                  password-reset links for this app are only returned to origins on it.
                </>
              ) : (
                <>
                  None set. Fine for a mobile or server-side client; a web app should
                  list where it is served from, or OAuth and password reset have nowhere
                  they are allowed to return to.
                </>
              )}
            </span>
          ) : (
            <span className="panel__chips" data-testid="urls-own">
              {own.map((o) => (
                <code className="mono chip" key={o}>
                  {o}
                </code>
              ))}
            </span>
          )}
          <button
            type="button"
            className="btn btn-quiet panel__btn"
            onClick={() => setEditing(true)}
            data-testid="urls-edit"
          >
            {own.length === 0 ? "Set URLs" : "Edit"}
          </button>
        </>
      ) : (
        <div className="panel__edit">
          <textarea
            className="panel__textarea mono"
            rows={3}
            value={draft}
            spellCheck={false}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            placeholder={"https://app.example.com\nhttp://localhost:3000"}
            aria-label="URLs, one per line"
            data-testid="urls-input"
          />
          <p className="sm muted">
            One per line, exact — no wildcards. This is also where an OAuth popup may
            return a session and where a password-reset link may be sent, so a{" "}
            <span className="mono">*.example.com</span> would turn any stale subdomain
            into a way to collect them. Include your{" "}
            <span className="mono">localhost</span> port if you use either flow while
            developing.
          </p>
          {err && (
            <p className="sm err" data-testid="urls-error">
              {err}
            </p>
          )}
          <div className="panel__actions">
            <button
              type="button"
              className="btn btn-solid panel__btn"
              onClick={() => void save()}
              disabled={busy}
              data-testid="urls-save"
            >
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="btn btn-quiet panel__btn"
              onClick={() => {
                setDraft(own.join("\n"));
                setEditing(false);
                setErr(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Which environment the list below is describing.
 *
 * Deliberately loud, and deliberately NOT a quiet dropdown among the other
 * filters. Every other control on this screen narrows what you see; this one
 * changes what the rows MEAN — whether the accounts under it are disposable test
 * data or real people. An operator who misreads it deletes the wrong thing, so
 * dev carries its own colour and says outright that everything in it can be wiped.
 */
function EnvBar({
  value,
  live,
  dev,
  onChange,
}: {
  value: EnvFilter;
  live: number;
  dev: number;
  onChange: (v: EnvFilter) => void;
}) {
  const tabs: { id: EnvFilter; label: string; n: number }[] = [
    { id: "all", label: "Everything", n: live + dev },
    { id: "live", label: "Live", n: live },
    { id: "dev", label: "Dev", n: dev },
  ];
  return (
    <div className={`envbar envbar--${value}`} data-testid="env-bar">
      <div className="segbar envbar__tabs" role="tablist" aria-label="Environment">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={value === t.id}
            className={value === t.id ? "on" : undefined}
            onClick={() => onChange(t.id)}
            data-testid={`env-${t.id}`}
          >
            {t.label}
            <span className="segbar__n">{t.n.toLocaleString()}</span>
          </button>
        ))}
      </div>
      <p className="sm envbar__note">
        {value === "dev" ? (
          <>
            <b>Everything here is disposable.</b> Dev accounts can be deleted
            individually or wiped in one go.
          </>
        ) : value === "live" ? (
          <>
            <b>Real people.</b> A live account can only be deleted if it is marked as
            a test account, and there is no way to wipe live in bulk.
          </>
        ) : (
          <>
            Both environments. Sign-ins from <span className="mono">localhost</span> are
            dev; everything else is live.
          </>
        )}
      </p>
    </div>
  );
}

/**
 * A count, and — where it means something — what it is a count OF.
 *
 * "412 confirmed email" invites the reading "412 of everyone". "412 / 908" does
 * not, and the gap is the number an operator actually needs.
 */
function Stat({ n, of, label, note }: { n: number; of?: number; label: string; note?: string }) {
  return (
    <div className="card pad ustat">
      <div className="stat-n">
        {n.toLocaleString()}
        {of !== undefined && <span className="stat-of"> / {of.toLocaleString()}</span>}
      </div>
      <div className="ustat__label">{label}</div>
      {note && <div className="ustat__note">{note}</div>}
    </div>
  );
}

function UserRow({
  api,
  user,
  apps,
  open,
  onToggle,
  onChanged,
  onDelete,
  appLabel,
}: {
  apps: TenantApp[];
  api: Api;
  user: EndUser;
  open: boolean;
  onToggle: () => void;
  onChanged: () => void;
  onDelete: () => void;
  /** Null on a single-app tenant, where naming the app would be noise. */
  appLabel: string | null;
}) {
  // The primary identifier is the account's name here — these accounts have no
  // usernames, and most will have no display name either.
  const title = user.name || user.email || user.phone || user.user_id;
  const [busy, setBusy] = useState(false);

  const toggleTest = async () => {
    setBusy(true);
    try {
      await api.updateUser(user.user_id, { is_test: !user.is_test });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr data-testid="user-row" className={user.is_test ? "is-test" : undefined}>
        <td>
          <span className="u-name">{title}</span>
          <span className="u-id mono">{user.user_id}</span>
          {user.status !== "active" && <span className="u-badge">{user.status}</span>}
        </td>
        {appLabel !== null && (
          <td className="u-col-app">
            <span className="u-app">{appLabel}</span>
          </td>
        )}
        <td>
          <span className="u-idents">
            {user.identifiers.length === 0 && <span className="sm muted">—</span>}
            {user.identifiers.map((i) => (
              <span className="u-ident" key={i.identifier_id}>
                <span className="mono sm">{i.value}</span>
                <span
                  className={`u-badge${i.verified ? " is-ok" : ""}`}
                  title={
                    i.verified
                      ? `Confirmed ${new Date(i.verified_at ?? "").toLocaleDateString()}`
                      : "Typed, never confirmed"
                  }
                >
                  {i.verified ? "confirmed" : "unconfirmed"}
                </span>
              </span>
            ))}
          </span>
        </td>
        <td className="u-when sm muted">
          {new Date(user.created_at).toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </td>
        <td className="u-col-act">
          <span className="u-actions">
            {/* Marking is the frequent action, so it sits in the row rather than
                behind the disclosure. */}
            <label className="checkchip checkchip--sm" title="Seeded or scratch data">
              <input
                type="checkbox"
                checked={user.is_test}
                disabled={busy}
                onChange={toggleTest}
                data-testid={`test-toggle-${user.user_id}`}
              />
              <span>Test</span>
            </label>
            <button
              className="u-disclose"
              type="button"
              aria-expanded={open}
              onClick={onToggle}
              data-testid={`expand-${user.user_id}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m6 9 6 6 6-6" />
              </svg>
              <span className="sr-only">Details</span>
            </button>
          </span>
        </td>
      </tr>
      {open && (
        <tr className="u-detail-row">
          <td colSpan={appLabel !== null ? 5 : 4}>
            <UserDetail
              api={api}
              user={user}
              apps={apps}
              onChanged={onChanged}
              onDelete={onDelete}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function UserDetail({
  api,
  user,
  apps,
  onChanged,
  onDelete,
}: {
  api: Api;
  user: EndUser;
  apps: TenantApp[];
  onChanged: () => void;
  onDelete: () => void;
}) {
  const pretty = (m: Record<string, unknown>) => JSON.stringify(m ?? {}, null, 2);
  const [draft, setDraft] = useState(() => pretty(user.metadata));
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const dirty = draft !== pretty(user.metadata);

  const save = async () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft || "{}");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That is not valid JSON.");
      return;
    }
    // The column is a JSON OBJECT. An array or a bare number is valid JSON and
    // still cannot be stored, so it is refused here with the actual reason rather
    // than as a 422 from the server.
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      setErr("Metadata must be a JSON object — { } rather than a list or a value.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.updateUser(user.user_id, { metadata: parsed as Record<string, unknown> });
      setSaved(true);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 2000);
    return () => clearTimeout(t);
  }, [saved]);

  return (
    <div className="udetail">
      <div className="udetail__pane">
        <div className="udetail__head">
          <span className="label">Metadata</span>
          <span className="sm muted">
            Any valid JSON object. Yours to use — nothing here is interpreted.
          </span>
        </div>
        <textarea
          className="input input-mono udetail__json"
          spellCheck={false}
          rows={6}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setErr(null);
          }}
          data-testid={`metadata-${user.user_id}`}
        />
        {err && <div className="err" role="alert">{err}</div>}
        <div className="udetail__actions">
          <button
            className="btn btn-solid"
            disabled={!dirty || busy}
            onClick={() => void save()}
            data-testid={`save-metadata-${user.user_id}`}
          >
            {busy ? "Saving…" : "Save metadata"}
          </button>
          {dirty && (
            <button className="btn btn-quiet" onClick={() => { setDraft(pretty(user.metadata)); setErr(null); }}>
              Reset
            </button>
          )}
          {saved && !dirty && <span className="sm udetail__ok">Saved.</span>}
        </div>
      </div>

      {apps.length > 1 && <Relocate api={api} user={user} apps={apps} onChanged={onChanged} />}

      <div className="udetail__pane udetail__danger">
        <div className="udetail__head">
          <span className="label">Delete</span>
        </div>
        {user.is_test ? (
          <>
            <p className="sm muted">
              Erases this account, every identifier on it, and every session. It
              cannot be undone, and the addresses become free to sign up again.
            </p>
            <button className="btn btn-danger" onClick={onDelete} data-testid={`delete-${user.user_id}`}>
              Delete account
            </button>
          </>
        ) : (
          <p className="sm muted">
            Only accounts marked <b>Test</b> can be deleted here. Marking one is a
            click and undoes cleanly; this does not. If you meant to remove a real
            person&rsquo;s account, that runs against the API with the tenant key.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Sending an account to another app.
 *
 * ── Two operations that look alike and are not ───────────────────────────────
 * **Move** keeps the account. Same id, different door — so anything your product
 * stored against that id still finds them, and the source app loses them. It is
 * reversible: move them back and you are exactly where you started.
 *
 * **Copy** cannot keep the id, because an account belongs to exactly one app —
 * that is what apps are. So it creates a SECOND person: same address, new id, no
 * history. Additive rather than destructive, undone by deleting the copy.
 *
 * Both are offered here, unlike promotion, precisely because of that: neither
 * crosses into a state there is no way back from. Promotion carries an account out
 * of the environment that can be wiped into the one that cannot, so it stays on
 * the master key and off this screen.
 *
 * The choice is made with two buttons rather than a mode toggle, because "does
 * this person's history follow them" is the question being answered and a toggle
 * lets it be answered by whichever position the control was left in.
 */
function Relocate({
  api,
  user,
  apps,
  onChanged,
}: {
  api: Api;
  user: EndUser;
  apps: TenantApp[];
  onChanged: () => void;
}) {
  const others = apps.filter((a) => a.app_id !== user.app_id && a.status !== "deleted");
  const [target, setTarget] = useState(others[0]?.app_id ?? "");
  const [busy, setBusy] = useState<"move" | "copy" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (others.length === 0) return null;
  const targetName = others.find((a) => a.app_id === target)?.name ?? "";

  const run = async (copy: boolean) => {
    setBusy(copy ? "copy" : "move");
    setErr(null);
    setDone(null);
    try {
      const r = await api.relocateAccounts(user.app_id, {
        user_ids: [user.user_id],
        to_app_id: target,
        copy,
      });
      const outcome = r.results[0];
      if (outcome?.outcome === "moved") setDone(`Moved to ${targetName}.`);
      else if (outcome?.outcome === "copied") setDone(`Copied into ${targetName}.`);
      else setErr(outcome?.detail ?? "That account could not be sent.");
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That did not work.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="udetail__pane" data-testid="relocate">
      <div className="udetail__head">
        <span className="label">Send to another app</span>
      </div>
      <select
        className="input"
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        aria-label="Destination app"
        data-testid="relocate-target"
      >
        {others.map((a) => (
          <option key={a.app_id} value={a.app_id}>
            {a.name}
          </option>
        ))}
      </select>
      <div className="panel__actions">
        <button
          className="btn panel__btn"
          disabled={!!busy}
          onClick={() => void run(false)}
          data-testid="relocate-move"
        >
          {busy === "move" ? "Moving…" : "Move"}
        </button>
        <button
          className="btn panel__btn"
          disabled={!!busy}
          onClick={() => void run(true)}
          data-testid="relocate-copy"
        >
          {busy === "copy" ? "Copying…" : "Copy"}
        </button>
      </div>
      <p className="sm muted">
        <b>Move</b> keeps this account and its id, so anything you stored against it
        still finds them — {user.app_id ? "the current app loses them" : "it changes door"}.{" "}
        <b>Copy</b> makes a second, separate account there: same address, new id, none
        of this one&rsquo;s history.
      </p>
      {done && (
        <p className="sm udetail__ok" data-testid="relocate-done">
          {done}
        </p>
      )}
      {err && (
        <p className="sm err" data-testid="relocate-error">
          {err}
        </p>
      )}
    </div>
  );
}

/**
 * The confirmation. Typing the account's own identifier, in the manner of AWS.
 *
 * A yes/no dialog is a reflex; typing something specific is a decision. The
 * string is the address or id shown in the row, so it also forces a last look at
 * WHICH account is about to go — which is the mistake this is really guarding
 * against, more than the click itself.
 */
/**
 * Type-the-name-to-confirm, for anything that cannot be undone.
 *
 * One component rather than one per destructive action, because the mechanics are
 * the part that is easy to get subtly wrong — the focus trap, Escape, the inert
 * backdrop, and the case-exactness of the phrase — and getting them right twice
 * independently is how the second copy ends up weaker than the first.
 */
function ConfirmDialog({
  title,
  body,
  phrase,
  confirmLabel,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: React.ReactNode;
  phrase: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLDialogElement>(null);

  // A real <dialog>, for the focus trap, the Escape key and the inert backdrop —
  // all of which a hand-rolled overlay has to reimplement and usually half-does.
  useEffect(() => {
    ref.current?.showModal();
  }, []);

  const go = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onConfirm();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <dialog
      className="confirm"
      ref={ref}
      onCancel={onCancel}
      onClose={onCancel}
      data-testid="confirm-dialog"
    >
      <h2>{title}</h2>
      <p className="sm">{body}</p>
      {/* NOT `.label` — that class uppercases, which told the operator to type
          STUDENT1@EXAMPLE.COM when the string it compares against is lowercase.
          A confirmation that misquotes the thing being confirmed is worse than
          no confirmation. */}
      <label className="confirm__ask" htmlFor="confirm-phrase">
        Type <span className="mono confirm__phrase">{phrase}</span> to confirm
      </label>
      <input
        id="confirm-phrase"
        className="input input-mono"
        value={typed}
        autoFocus
        spellCheck={false}
        onChange={(e) => setTyped(e.target.value)}
        data-testid="confirm-input"
      />
      {err && <div className="err" role="alert">{err}</div>}
      <div className="confirm__actions">
        <button className="btn btn-quiet" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-danger"
          disabled={typed !== phrase || busy}
          onClick={() => void go()}
          data-testid="confirm-delete"
        >
          {busy ? "Working…" : confirmLabel}
        </button>
      </div>
    </dialog>
  );
}

function DeleteDialog({
  api,
  user,
  onClose,
  onDone,
}: {
  api: Api;
  user: EndUser;
  onClose: () => void;
  onDone: () => void;
}) {
  const phrase = user.email || user.phone || user.user_id;
  return (
    <ConfirmDialog
      title="Delete this account?"
      phrase={phrase}
      confirmLabel="Delete account"
      onCancel={onClose}
      onConfirm={async () => {
        await api.deleteUser(user.user_id);
        onDone();
      }}
      body={
        <>
          This erases <b>{phrase}</b>, every identifier on the account, and every
          session. <b>It cannot be undone.</b>
        </>
      }
    />
  );
}
